import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { SUPPORTED_LANGUAGES } from "@/i18n/resolve-language";

interface LanguageSwitcherProps {
  /** `onDark` inverts the palette for the login page's brand panel. */
  tone?: "default" | "onDark";
  /** Two-letter codes instead of language names, for tight bars like the
   *  marketing nav at phone widths. */
  compact?: boolean;
}

function labelKeyFor(lng: string, compact: boolean): string {
  if (lng === "th") return compact ? "language.thaiShort" : "language.thai";
  return compact ? "language.englishShort" : "language.english";
}

/**
 * TH/EN toggle. Mounted on public surfaces too, so a visitor who cannot read
 * Thai can switch before signing in.
 */
export function LanguageSwitcher({ tone = "default", compact = false }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const onDark = tone === "onDark";

  return (
    <fieldset
      aria-label={t("language.switchLabel")}
      className={clsx(
        "m-0 inline-flex min-w-0 items-center gap-0.5 rounded-full border-0 p-0.5",
        onDark ? "bg-white/15" : "bg-surface-secondary",
      )}
    >
      {SUPPORTED_LANGUAGES.map((lng) => {
        const active = lng === language;
        return (
          <button
            key={lng}
            type="button"
            aria-pressed={active}
            onClick={() => setLanguage(lng)}
            className={clsx(
              "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
              active && "bg-white text-text",
              active && !onDark && "shadow-sm",
              !active && !onDark && "text-text-muted hover:text-text",
              !active && onDark && "text-white/70 hover:text-white",
            )}
          >
            {t(labelKeyFor(lng, compact))}
          </button>
        );
      })}
    </fieldset>
  );
}
